import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ChevronLeft, ChevronRight, Send, Loader } from "lucide-react";
import api from "../services/api";
import { toast } from "react-toastify";
import { useMessageSendingProgress } from "../hooks/useMessageSendingProgress";
import MessageSendingProgress from "../components/MessageSendingProgress";
import Steps from "../components/broadcast/Steps";
import StepGroup from "../components/broadcast/StepGroup";
import StepTemplate from "../components/broadcast/StepTemplate";
import StepPreview from "../components/broadcast/StepPreview";

// ── Main page ─────────────────────────────────────────────────────────────────
const BroadcastCreate = () => {
  const navigate  = useNavigate();
  const location  = useLocation();

  // If navigated from the chat view with a pre-selected group, skip Step 1
  const preGroup  = location.state?.group ?? null;

  const [step, setStep]                         = useState(preGroup ? 2 : 1);
  const [selectedGroup,    setSelectedGroup]    = useState(preGroup);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [bodyParams,       setBodyParams]       = useState([]);
  const [metaBody,         setMetaBody]         = useState(null); // raw body text from Meta API
  const [broadcastName,    setBroadcastName]    = useState("");

  const [sending,     setSending]    = useState(false);
  const [activeJobId, setActiveJobId] = useState(() => localStorage.getItem("broadcastJobId") || null);

  const { progress } = useMessageSendingProgress(activeJobId);

  const canNext =
    (step === 1 && !!selectedGroup) ||
    (step === 2 && !!selectedTemplate);

  const handleNext = () => setStep((s) => s + 1);
  const handleBack = () => setStep((s) => s - 1);

  const handleSend = async () => {
    if (sending) return;
    setSending(true);
    try {
      // Build filled body text for the DB snapshot (replaces {{1}}, {{2}}… with actual values)
      const filledBody = metaBody
        ? bodyParams.reduce(
            (t, v, i) => t.replace(new RegExp(`\\{\\{${i + 1}\\}\\}`, "g"), v || `{{${i + 1}}}`),
            metaBody
          )
        : null;
      const r = await api.post("/broadcasts", {
        name: broadcastName || null,
        templateId: selectedTemplate.id,
        groupId: selectedGroup.id,
        dynamicData: { bodyParams },
        bodyText: filledBody,
      });
      const { jobId } = r.data;
      localStorage.setItem("broadcastJobId", jobId);
      setActiveJobId(jobId);
      toast.success("Broadcast started!");
    } catch (err) {
      toast.error(err?.response?.data?.error || "Failed to start broadcast");
      setSending(false);
    }
  };

  const handleProgressClose = () => {
    localStorage.removeItem("broadcastJobId");
    setActiveJobId(null);
    setSending(false);
    navigate("/broadcast");
  };

  useEffect(() => {
    if (progress?.status === "completed" || progress?.status === "failed") {
      setTimeout(() => {
        localStorage.removeItem("broadcastJobId");
        navigate("/broadcast");
      }, 4000);
    }
  }, [progress?.status, navigate]);

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      {/* Page header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate("/broadcast")}
          className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-xl font-bold text-gray-900">New Broadcast</h1>
          <p className="text-sm text-gray-500">
            {preGroup
              ? <>To: <span className="font-medium text-gray-700">{preGroup.name}</span></>
              : "Send a template message to a customer group"}
          </p>
        </div>
      </div>

      <Steps current={step} />

      {/* Step content */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sm:p-6 mb-6">
        {step === 1 && (
          <StepGroup selected={selectedGroup} onSelect={setSelectedGroup} />
        )}
        {step === 2 && (
          <StepTemplate
            selected={selectedTemplate}
            onSelect={setSelectedTemplate}
            bodyParams={bodyParams}
            onParamsChange={setBodyParams}
            onMetaBodyChange={setMetaBody}
          />
        )}
        {step === 3 && (
          <StepPreview
            template={selectedTemplate}
            group={selectedGroup}
            bodyParams={bodyParams}
            metaBody={metaBody}
            broadcastName={broadcastName}
            onNameChange={setBroadcastName}
          />
        )}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <button
          onClick={
            step === 1 ? () => navigate("/broadcast")
            : step === 2 && preGroup ? () => navigate("/broadcast")
            : handleBack
          }
          className="flex items-center gap-1.5 px-4 py-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
          {step === 1 || (step === 2 && preGroup) ? "Cancel" : "Back"}
        </button>

        {step < 3 ? (
          <button
            onClick={handleNext}
            disabled={!canNext}
            className="flex items-center gap-1.5 px-5 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-40 rounded-xl transition-colors"
          >
            Next
            <ChevronRight className="w-4 h-4" />
          </button>
        ) : (
          <button
            onClick={handleSend}
            disabled={sending || !!activeJobId}
            className="flex items-center gap-2 px-6 py-2 text-sm font-medium text-white bg-green-500 hover:bg-green-600 disabled:opacity-50 rounded-xl transition-colors"
          >
            {sending ? <Loader className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Send Broadcast
          </button>
        )}
      </div>

      {/* Progress widget */}
      {activeJobId && (
        <MessageSendingProgress
          isVisible={true}
          totalMessages={progress?.totalMessages ?? 0}
          sentMessages={progress?.sentMessages ?? 0}
          failedMessages={progress?.failedMessages ?? 0}
          estimatedTimeRemaining={progress?.estimatedTimeRemaining ?? 0}
          status={progress?.status ?? "sending"}
          onClose={handleProgressClose}
        />
      )}
    </div>
  );
};

export default BroadcastCreate;
